// Centralized design tokens for the brand app.
//
// Ported from the creator app (reel-campaign) so a brand and a creator looking
// at the same affiliate programme see one product, not two. Keep the export
// names in step with that copy — the shared components below are written
// against these keys, and a rename here is a rename there.
//
// Use these instead of hardcoded hex values inside screens.

export const palette = {
  brand: '#F14950',
  brandDark: '#D83A40',
  brandSoft: '#FFE9EA',
  brandSofter: '#FFF5F5',

  ink: '#0F1729',
  inkSoft: '#3A4256',
  muted: '#6B7280',
  hint: '#9AA1AE',
  line: '#E5E7EB',
  surface: '#FFFFFF',
  surfaceAlt: '#FFF8F8',
  surfaceCanvas: '#FFF5F5',

  success: '#10B981',
  successSoft: '#DCFCE7',
  warning: '#F59E0B',
  warningSoft: '#FEF3C7',
  info: '#3B82F6',
  infoSoft: '#DBEAFE',
  danger: '#EF4444',
  dangerSoft: '#FEE2E2',
  purple: '#8B5CF6',
  purpleSoft: '#EDE9FE',
  teal: '#14B8A6',
  tealSoft: '#CCFBF1',
};

// Status colors for entity status pills. One vocabulary across the product, so
// a state is coloured the same wherever it is shown.
export const statusColors = {
  DRAFT:     { fg: '#6B7280', bg: '#F3F4F6' },
  PUBLISHED: { fg: '#065F46', bg: '#DCFCE7' },
  ACTIVE:    { fg: '#065F46', bg: '#DCFCE7' },
  ENDED:     { fg: '#1E3A8A', bg: '#DBEAFE' },
  PAUSED:    { fg: '#92400E', bg: '#FEF3C7' },
  CANCELLED: { fg: '#9F1239', bg: '#FEE2E2' },
  ARCHIVED:  { fg: '#9F1239', bg: '#FEE2E2' },
  PENDING:   { fg: '#92400E', bg: '#FEF3C7' },
  PAID:      { fg: '#065F46', bg: '#DCFCE7' },
  REFUNDED:  { fg: '#9F1239', bg: '#FEE2E2' },
  SENT:      { fg: '#065F46', bg: '#DCFCE7' },
  FAILED:    { fg: '#9F1239', bg: '#FEE2E2' },
  VERIFIED:  { fg: '#065F46', bg: '#DCFCE7' },
  REJECTED:  { fg: '#9F1239', bg: '#FEE2E2' },
};

export const radius = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  pill: 999,
};

export const shadow = {
  card:   '0 1px 2px rgba(15, 23, 41, 0.04), 0 1px 3px rgba(15, 23, 41, 0.06)',
  hover:  '0 8px 24px rgba(15, 23, 41, 0.08), 0 2px 6px rgba(15, 23, 41, 0.04)',
  modal:  '0 24px 48px rgba(15, 23, 41, 0.12), 0 6px 16px rgba(15, 23, 41, 0.08)',
  inset:  'inset 0 0 0 1px rgba(15, 23, 41, 0.06)',
  glow:   '0 0 0 4px rgba(241, 73, 80, 0.12)',
};

// All module gradients use the brand red so the theme stays consistent.
export const gradients = {
  brand:    'linear-gradient(135deg, #F14950 0%, #FF7A82 100%)',
  hero:     'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 50%, #FFFFFF 100%)',
  commerce: 'linear-gradient(135deg, #FFE9EA 0%, #FFF5F5 100%)',
};

export const typography = {
  pageTitle:    { fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: palette.ink },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: palette.ink },
  cardTitle:    { fontSize: 15, fontWeight: 700, color: palette.ink },
  body:         { fontSize: 14, color: palette.inkSoft },
  caption:      { fontSize: 12, color: palette.muted },
  micro:        { fontSize: 11, color: palette.hint, letterSpacing: '0.04em', textTransform: 'uppercase' },
  // Small all-caps label used above field groups and metric values.
  eyebrow:      { fontSize: 11, fontWeight: 700, color: palette.muted, letterSpacing: '0.07em', textTransform: 'uppercase' },
  // Figures read down a column rather than across a sentence: tabular digits
  // so the decimal points line up between rows of different magnitudes.
  money:        { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' },
  mono:         { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 },
};

// Shared easing/duration so every hover, expand and transition in the product
// moves the same way. `base` is the default for colour + shadow changes,
// `emphasis` for anything that also moves in space.
export const motion = {
  fast:     '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base:     '180ms cubic-bezier(0.4, 0, 0.2, 1)',
  emphasis: '260ms cubic-bezier(0.16, 1, 0.3, 1)',
};

// Keyboard-visible focus ring. Applied via `&:focus-visible` so pointer users
// never see it but keyboard users always do.
export const focusRing = {
  outline: 'none',
  boxShadow: `0 0 0 2px ${palette.surface}, 0 0 0 4px rgba(241, 73, 80, 0.45)`,
};
